import React, { useState, useRef, useEffect } from "react";
import {
    Table,
    TableHeader,
    TableBody,
    TableHead,
    TableRow,
    TableCell,
} from "@shadcn-in-nx/ui/table";
import { FileText, X, Search, ChevronLeft, ChevronRight } from "lucide-react";
import * as pdfjsLib from "pdfjs-dist";
import * as pdfWorker from "pdfjs-dist/build/pdf.worker.mjs";

// pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;
import { Bluelagoon } from "@bluelagoon";
import axios from "axios";

/**
 * Component that displays an array of objects in a table and allows viewing PDFs
 * with text highlighting when clicking on rows with URL/fileUrl properties.
 *
 * @param {Object} props
 * @param {Array} props.data - Array of objects to display in the table
 * @param {string[]} props.columns - Optional array of column keys to display (if not provided, all keys will be used)
 * @param {string} props.urlField - Name of the field containing the PDF URL (default: "url" or "fileUrl")
 * @param {string} props.textField - Name of the field containing the text to highlight (default: "text" or "content")
 * @param {number} props.pageSize - Number of items per page for pagination (default: 10)
 * @param {string} props.title - Optional title for the table
 */
const PDFDocumentTable = ({
    data = [],
    columns = [],
    urlField = "",
    textField = "",
    pageSize = 10,
    title = "Document Table",
    config = {},
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedRow, setSelectedRow] = useState(null);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [pdfDocument, setPdfDocument] = useState(null);
    const [currentPdfPage, setCurrentPdfPage] = useState(1);
    const [totalPdfPages, setTotalPdfPages] = useState(0);
    const [pdfPageText, setPdfPageText] = useState("");
    const [highlightedText, setHighlightedText] = useState([]);
    const [loading, setLoading] = useState(false);
    const [scale, setScale] = useState(1.2);

    const canvasRef = useRef(null);
    const textLayerRef = useRef(null);

    // Determine which fields to use for URL and text if not provided
    const effectiveUrlField =
        urlField ||
        getFirstMatchingField(data, ["url", "fileUrl", "pdfUrl", "archivos"]);
    const effectiveTextField =
        textField ||
        getFirstMatchingField(data, ["text", "content", "extractedText"]);

    // Calculate pagination
    const totalPages = Math.ceil(data.length / pageSize);
    const paginatedData = data.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    // Determine table columns if not provided
    const tableColumns =
        columns.length > 0
            ? columns
            : data.length > 0
            ? Object.keys(data[0]).filter(
                  (key) =>
                      typeof data[0][key] !== "object" || data[0][key] === null
              )
            : [];

    // Helper function to find the first matching field in the data
    function getFirstMatchingField(data, possibleFields) {
        if (!data || data.length === 0) return "";

        const sampleItem = data[0];
        return possibleFields.find((field) => field in sampleItem) || "";
    }

    // Handle row click to view PDF
    const handleRowClick = async (row) => {
        const colValue = row[effectiveUrlField];

        let url = colValue;

        if (Array.isArray(colValue)) {
            url = colValue[0];
        }

        if (url) {
            try {
                setLoading(true);
                const response = await axios({
                    method: "GET",
                    url: `${Bluelagoon.apiUrl}/proxy/files/signed-url`,
                    headers: {
                        Authorization: `Bearer ${Bluelagoon.getToken()}`,
                        "x-api-key": config.apiKey,
                    },
                    params: {
                        fileUrl: url,
                    },
                });

                const signedUrl = response.data.url;
                setLoading(false);
                setSelectedRow(row);
                setPdfUrl(signedUrl);
                setCurrentPdfPage(1);
                const valuesToHighlight = [];
                Object.values(row).map((value) => {
                    if (typeof value === "string" && value.length > 3) {
                        value.split(" ").forEach((word) => {
                            if (word.length > 1) {
                                valuesToHighlight.push(word);
                            }
                        });
                    }
                });
                console.log("valuesToHighlight", valuesToHighlight);
                setHighlightedText((prev) => valuesToHighlight);
            } catch (error) {
                console.error("Error getting signed URL:", error);
            } finally {
                setLoading(false);
            }
        }
    };

    // Close PDF viewer
    const closePdfViewer = () => {
        setPdfUrl(null);
        setPdfDocument(null);
        setSelectedRow(null);
    };

    // Load PDF document when URL changes
    useEffect(() => {
        if (!pdfUrl) return;

        const loadPdf = async () => {
            try {
                setLoading(true);
                const loadingTask = pdfjsLib.getDocument(pdfUrl);
                const pdf = await loadingTask.promise;
                setPdfDocument(pdf);
                setTotalPdfPages(pdf.numPages);
                setLoading(false);
            } catch (error) {
                console.error("Error loading PDF:", error);
                setLoading(false);
            }
        };

        loadPdf();
    }, [pdfUrl]);

    // Render PDF page when current page changes
    useEffect(() => {
        if (!pdfDocument) return;

        const renderPage = async () => {
            try {
                setLoading(true);

                // Get the page
                const page = await pdfDocument.getPage(currentPdfPage);

                if (canvasRef.current) {
                    // Set up canvas
                    const canvas = canvasRef.current;
                    const context = canvas.getContext("2d");

                    // Calculate viewport with scale
                    const viewport = page.getViewport({ scale });

                    // Set canvas dimensions to match viewport
                    canvas.height = viewport.height;
                    canvas.width = viewport.width;

                    // Render the page
                    const renderContext = {
                        canvasContext: context,
                        viewport: viewport,
                    };

                    await page.render(renderContext).promise;

                    // Extract text content for highlighting
                    const textContent = await page.getTextContent();
                    const textItems = textContent.items;
                    let fullText = "";

                    textItems.forEach((item) => {
                        fullText += item.str + " ";
                    });

                    setPdfPageText(fullText);

                    // Create text layer and highlight text
                    const textLayer = textLayerRef.current;
                    textLayer.innerHTML = "";
                    textLayer.style.left = "0";
                    textLayer.style.top = "0";
                    textLayer.style.height = `${viewport.height}px`;
                    textLayer.style.width = `${viewport.width}px`;

                    // Add text positions for highlighting
                    textItems.forEach((item) => {
                        const transform = viewport.transform;
                        const [a, b, c, d, e, f] = item.transform;

                        // Calculate position using the transform matrix
                        const tx =
                            transform[0] * e + transform[2] * f + transform[4];
                        const ty =
                            transform[1] * e + transform[3] * f + transform[5];

                        const textSpan = document.createElement("span");
                        textSpan.textContent = item.str;
                        textSpan.style.position = "absolute";
                        textSpan.style.left = `${tx - 2}px`;
                        textSpan.style.top = `${ty - 4}px`;
                        textSpan.style.fontSize = `${
                            item.height * viewport.scale
                        }px`;
                        textSpan.style.fontFamily = item.fontName;
                        textSpan.style.transform = `scaleX(${
                            item.width / (item.str.length * item.height)
                        })`;
                        textSpan.style.transformOrigin = "left top";
                        textSpan.style.whiteSpace = "pre";
                        textSpan.style.color = "transparent";
                        textSpan.style.padding = "2px 10px";
                        textSpan.style.margin = "-8px 0px";

                        // Add highlighting if this text matches the highlighted text
                        if (
                            highlightedText &&
                            highlightedText.some((text) =>
                                item.str.includes(text)
                            )
                        ) {
                            textSpan.style.backgroundColor =
                                "rgba(255, 255, 0, 0.5)";
                            textSpan.style.color = "transparent";
                        }

                        textLayer.appendChild(textSpan);
                    });

                    setLoading(false);
                }
            } catch (error) {
                console.error("Error rendering PDF page:", error);
                setLoading(false);
            }
        };

        renderPage();
    }, [pdfDocument, currentPdfPage, scale, highlightedText]);

    // Handle navigation to previous PDF page
    const goToPreviousPage = () => {
        if (currentPdfPage > 1) {
            setCurrentPdfPage(currentPdfPage - 1);
        }
    };

    // Handle navigation to next PDF page
    const goToNextPage = () => {
        if (currentPdfPage < totalPdfPages) {
            setCurrentPdfPage(currentPdfPage + 1);
        }
    };

    // Handle zoom in
    const zoomIn = () => {
        setScale((prevScale) => prevScale + 0.2);
    };

    // Handle zoom out
    const zoomOut = () => {
        setScale((prevScale) => Math.max(0.6, prevScale - 0.2));
    };

    // Handle pagination for table
    const goToPage = (page) => {
        setCurrentPage(page);
    };

    return (
        <div className="w-full space-y-4">
            {/* Table Title */}
            {title && <h2 className="text-xl font-semibold">{title}</h2>}

            <div className={`flex ${pdfUrl ? "gap-4" : ""}`}>
                {/* Data Table */}
                <div
                    className={`rounded-md border ${
                        pdfUrl ? "w-1/2" : "w-full"
                    }`}
                >
                    <Table>
                        <TableHeader>
                            <TableRow>
                                {tableColumns.map((column) => (
                                    <TableHead
                                        key={column}
                                        className="px-4 py-3 text-left font-medium"
                                    >
                                        {column}
                                    </TableHead>
                                ))}
                                <TableHead className="w-10"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {paginatedData.length > 0 ? (
                                paginatedData.map((row, index) => (
                                    <TableRow
                                        key={index}
                                        className={`cursor-pointer hover:bg-gray-50 ${
                                            row[effectiveUrlField]
                                                ? "hover:bg-blue-50"
                                                : ""
                                        } ${
                                            selectedRow === row
                                                ? "bg-blue-50"
                                                : ""
                                        }`}
                                        onClick={() => handleRowClick(row)}
                                    >
                                        {tableColumns.map((column) => (
                                            <TableCell
                                                key={`${index}-${column}`}
                                                className="px-4 py-3"
                                            >
                                                {typeof row[column] ===
                                                    "string" ||
                                                typeof row[column] === "number"
                                                    ? row[column]
                                                    : JSON.stringify(
                                                          row[column]
                                                      )}
                                            </TableCell>
                                        ))}
                                        <TableCell className="px-4 py-3">
                                            {row[effectiveUrlField] && (
                                                <FileText className="h-5 w-5 text-blue-500" />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell
                                        colSpan={tableColumns.length + 1}
                                        className="text-center py-4"
                                    >
                                        No data available
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>

                {/* PDF Viewer */}
                {pdfUrl && (
                    <div className="w-1/2 bg-white rounded-lg shadow-lg flex flex-col">
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 border-b">
                            <div className="font-medium">
                                {selectedRow &&
                                    tableColumns[0] &&
                                    selectedRow[tableColumns[0]]}
                            </div>
                            <div className="flex items-center gap-4">
                                <button
                                    className="p-2 hover:bg-gray-100 rounded-full"
                                    onClick={zoomOut}
                                >
                                    -
                                </button>
                                <span>{Math.round(scale * 100)}%</span>
                                <button
                                    className="p-2 hover:bg-gray-100 rounded-full"
                                    onClick={zoomIn}
                                >
                                    +
                                </button>
                                <button
                                    className="p-2 hover:bg-gray-100 rounded-full"
                                    onClick={closePdfViewer}
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                        </div>

                        {/* PDF Content */}
                        <div className="flex-1 overflow-auto p-4 flex flex-col items-center">
                            <div className="relative">
                                <canvas
                                    ref={canvasRef}
                                    className="shadow-lg"
                                ></canvas>
                                <div
                                    ref={textLayerRef}
                                    className="absolute top-0 left-0 pointer-events-none"
                                ></div>
                            </div>
                        </div>

                        {/* PDF Navigation */}
                        <div className="flex items-center justify-between p-4 border-t">
                            <button
                                className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-50 flex items-center gap-1"
                                onClick={goToPreviousPage}
                                disabled={currentPdfPage === 1}
                            >
                                <ChevronLeft className="h-4 w-4" />
                                Previous
                            </button>

                            <div className="text-sm">
                                Page {currentPdfPage} of {totalPdfPages}
                            </div>

                            <button
                                className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-50 flex items-center gap-1"
                                onClick={goToNextPage}
                                disabled={currentPdfPage === totalPdfPages}
                            >
                                Next
                                <ChevronRight className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Table Pagination */}
            {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-4">
                    <button
                        className="px-3 py-1 rounded border hover:bg-gray-100 disabled:opacity-50"
                        onClick={() => goToPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                    >
                        Previous
                    </button>

                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        // Show pagination with current page in the middle when possible
                        let pageNum;
                        if (totalPages <= 5) {
                            pageNum = i + 1;
                        } else if (currentPage <= 3) {
                            pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                        } else {
                            pageNum = currentPage - 2 + i;
                        }

                        return (
                            <button
                                key={pageNum}
                                className={`px-3 py-1 rounded ${
                                    pageNum === currentPage
                                        ? "bg-blue-500 text-white"
                                        : "border hover:bg-gray-100"
                                }`}
                                onClick={() => goToPage(pageNum)}
                            >
                                {pageNum}
                            </button>
                        );
                    })}

                    <button
                        className="px-3 py-1 rounded border hover:bg-gray-100 disabled:opacity-50"
                        onClick={() =>
                            goToPage(Math.min(totalPages, currentPage + 1))
                        }
                        disabled={currentPage === totalPages}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

export default PDFDocumentTable;
